import { keyframes } from '@emotion/react';
import { FC } from 'react';
import { MdClose } from 'react-icons/md';
import { MCustomIconButton } from '../Monetize';
import { MBox, MFlex } from '../Monetize/chakra';

const bubbleEntrance = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/**
 * Half-width (and height) of the pointer triangle tying the bubble to the
 * launcher. `ARROW_OFFSET` centres it over the 3rem launcher, whose own
 * centre sits 1.5rem from the right edge.
 */
const ARROW_SIZE = '8px';
const ARROW_OFFSET = `calc(1.5rem - ${ARROW_SIZE})`;

/**
 * A downward CSS triangle: zero-size box, transparent side borders, and only
 * the top border painted. Every side is set longhand -- Chakra resolves
 * `borderColor` through its colour-token scale, so a four-value shorthand
 * silently fails to apply and leaves a filled square instead.
 */
const arrowTriangle = (color: string) => ({
  content: '""',
  position: 'absolute' as const,
  top: '100%',
  right: ARROW_OFFSET,
  width: 0,
  height: 0,
  borderStyle: 'solid',
  borderTopWidth: ARROW_SIZE,
  borderRightWidth: ARROW_SIZE,
  borderBottomWidth: 0,
  borderLeftWidth: ARROW_SIZE,
  borderTopColor: color,
  borderRightColor: 'transparent',
  borderBottomColor: 'transparent',
  borderLeftColor: 'transparent',
});

export interface ChatLauncherNudgeProps {
  message: string;
  onDismiss: () => void;
  onDisableHints: () => void;
  onOpenChat: () => void;
  reducedMotion: boolean;
}

/**
 * One-time speech bubble that introduces Monty above the chat launcher.
 *
 * Rendered as an aside rather than a live region: it is an unprompted
 * suggestion, so announcing it would interrupt whatever the user is actually
 * doing. Keyboard users reach the launcher itself, which carries the same
 * affordance via its tooltip.
 */
export const ChatLauncherNudge: FC<ChatLauncherNudgeProps> = ({
  message,
  onDismiss,
  onDisableHints,
  onOpenChat,
  reducedMotion,
}) => (
  <MBox
    as="aside"
    position="absolute"
    bottom="calc(100% + 0.75rem)"
    right={0}
    width="15rem"
    bg="bg.surface"
    borderWidth="1px"
    borderColor="border.subtle"
    borderRadius="lg"
    boxShadow="0 6px 20px rgba(140, 50, 255, 0.18)"
    px={3}
    py={2.5}
    animation={reducedMotion ? undefined : `${bubbleEntrance} 240ms ease-out`}
    data-testid="chat-launcher-nudge"
    // Two stacked triangles: the lower one in the card's border colour,
    // the upper one shifted up 1px in the surface colour, so the arrow
    // carries the same hairline outline as the bubble it hangs off.
    _before={arrowTriangle('border.subtle')}
    _after={{ ...arrowTriangle('bg.surface'), top: 'calc(100% - 1px)' }}
  >
    <MFlex alignItems="flex-start" justifyContent="space-between" gap={2}>
      <MBox
        asChild
        fontSize="sm"
        color="fg.primary"
        textAlign="left"
        lineHeight="1.45"
        cursor="pointer"
        onClick={onOpenChat}
        data-testid="chat-launcher-nudge-open-btn"
      >
        <button type="button">{message}</button>
      </MBox>
      <MCustomIconButton
        aria-label="Dismiss hint"
        data-testid="chat-launcher-nudge-dismiss-btn"
        icon={MdClose}
        btnSize={3}
        containerSize={5}
        onClick={onDismiss}
      />
    </MFlex>
    <MBox
      asChild
      mt={1.5}
      fontSize="xs"
      color="fg.subtle"
      cursor="pointer"
      _hover={{ color: 'fg.muted', textDecoration: 'underline' }}
      onClick={onDisableHints}
      data-testid="chat-launcher-nudge-disable-btn"
    >
      <button type="button">Don't show hints</button>
    </MBox>
  </MBox>
);
